/**
 * Who is on the Board now, read from the document's awareness states. The
 * server stamps each state's `user` with the person's real identity, so the
 * names here cannot be spoofed by another browser.
 *
 * @module components/spaces/board/boardPresence
 */

export interface BoardPresenceEntry {
  /** Stable per person, so two tabs of one person show once. */
  readonly key: string;
  readonly name: string;
  readonly initials: string;
  readonly color: string;
}

const FALLBACK_COLOR = "#64748b";
const COLOR = /^#[0-9a-f]{3,8}$/i;

export function presenceInitials(name: string): string {
  const words = name
    .replace(/@.*$/, "")
    .split(/[\s._-]+/)
    .filter((word) => word.length > 0);
  if (words.length === 0) return "?";
  const letters = words.length === 1 ? words[0]!.slice(0, 2) : words[0]![0]! + words.at(-1)![0]!;
  return letters.toUpperCase();
}

/** Everyone but this browser, one entry per person, in a stable order. */
export function boardPresence(
  states: ReadonlyMap<number, Record<string, unknown>>,
  ownClientId: number,
): BoardPresenceEntry[] {
  const byKey = new Map<string, BoardPresenceEntry>();
  for (const [clientId, state] of states) {
    if (clientId === ownClientId) continue;
    const user = state["user"];
    if (user === null || typeof user !== "object") continue;
    const { id, name, color } = user as Record<string, unknown>;
    if (typeof name !== "string" || name.trim() === "") continue;
    const key = typeof id === "string" && id !== "" ? id : `client:${clientId}`;
    if (byKey.has(key)) continue;
    byKey.set(key, {
      key,
      name: name.trim(),
      initials: presenceInitials(name.trim()),
      color: typeof color === "string" && COLOR.test(color) ? color : FALLBACK_COLOR,
    });
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
}
